import { isFunction, INgModelController, isArray, IPromise, ITimeoutService } from 'angular';
import { defaultStrings } from './DefaultStrings';
import { Directive, Input, OnInit } from '@vbrick/angular-ts-decorators';
import { IInsightOptions, IInsightRecord } from './Insight.Contract';

interface IInfiniteScrollPage {
	results: any[];
	complete: boolean;
}

@Directive({
	selector: 'insight',
	template: `
		<div ng-include="$ctrl.templateUrl || 'insight.html'">
		</div>
	`,
	controllerAs: '$ctrl',
	scope: {},
	require: {
		ngModelCtrl: '?ngModel'
	}
})
export class InsightDirective implements OnInit {
	@Input() public insight: IInsightOptions;
	@Input() public templateUrl: string;
	@Input() public optionTemplateUrl: string;
	@Input() public assignedOptionTemplateUrl: string;

	private ngModelCtrl: INgModelController;
	private state: any;
	private assignedItems: IInsightRecord[];
	private filteredAssignedItems: IInsightRecord[];
	private filteredOptions: IInsightRecord[];
	private areAssignedOptionsLoaded: boolean;
	private showOptions: boolean;
	private focus: boolean;
	private totalItems: number;
	private isLoading: boolean;
	private numLoaded: number = 0;

	public get pauseAssignedInfiniteScroll(): boolean {
		return this.isLoading || this.areAssignedOptionsLoaded;
	}

	constructor(
		private $timeout: ITimeoutService,
		private $q,
		private filterFilter,
		private orderByFilter
	) {
		'ngInject';
	}

	public ngOnInit(): void {

		this.areAssignedOptionsLoaded = !isFunction(this.insight.onAssignedInfiniteScroll);
		this.insight.data = this.insight.data || [];
		this.insight.data.forEach(item => item._loaded = true);
		this.insight.strings = Object.assign({}, defaultStrings, this.insight.strings);

		if(!this.insight.fieldDefs){
			throw new Error('Insight fieldDefs is required');
		}

		this.insight.fieldDefs.orderBy = this.insight.fieldDefs.orderBy || this.insight.fieldDefs.display;

		this.state = {};
		this.state.noResults = false;

		this.assignedItems = [];
		this.filteredAssignedItems = [];

		Object.assign(this.ngModelCtrl, {
			$render: () => this.render(),
			$isEmpty: (value: IInsightRecord[]) => !value || !value.length
		});

	}
	private render(): void {
		this.assignedItems = [];

		this.insight.data.forEach(item => item.assigned = false);

		if(isArray(this.ngModelCtrl.$modelValue)){
			const items = this.ngModelCtrl.$modelValue
				.map(item => this.find(this.insight.data, item) || item);

			this.assignItems(items, true);
		}
	}

	private updateOptions(): void {
		if(isFunction(this.insight.loadQueryPage)){
			this.loadQueryPage()
				.then(options => {
					this.filteredOptions = this.filterOptions(options);
				});
		} else {
			this.filteredOptions = this.filterOptions(this.insight.data);
			this.state.noResults = !this.filteredOptions.length;
		}
	}

	private preventEnter($event: KeyboardEvent): void {
		if ($event.code === 'Enter') {
			$event.preventDefault();
		}
	}

	private loadAssignedPage(): IPromise<void> {
		if (this.pauseAssignedInfiniteScroll) {
			return;
		}

		this.isLoading = true;
		return this.$q.when(this.insight.onAssignedInfiniteScroll())
			.then((resultPage: IInsightRecord[] | IInfiniteScrollPage) => {
				const results = isArray(resultPage) ? resultPage : resultPage.results;
				const complete = !isArray(resultPage) && resultPage.complete;

				const loadedItems: IInsightRecord[] = [];
				const items = (results || []).map(item => {
					const existing = this.find(this.insight.data, item);
					const loaded = existing && existing._loaded;
					const updated = Object.assign(existing || {}, item, { _loaded: true });

					if(!loaded) {
						loadedItems.push(updated);
					}
					return existing ? null : updated;
				})
				.filter(Boolean);

				this.insight.data.push(...items);
				this.assignedItems.push(...items);

				//Maintain infinite scroll cursor position
				this.filteredAssignedItems = [
					...this.filteredAssignedItems,
					...this.sort(loadedItems)
				];

				this.numLoaded += loadedItems.length;
				if(this.numLoaded >= this.assignedItems.length || complete) {
					this.areAssignedOptionsLoaded = true;
					//If any records are still unloaded, show them
					this.filteredAssignedItems.push(...this.insight.data.filter(r => !r._loaded));
				}

				this.$timeout(() => this.isLoading = false, 100);
			});
	}

	private loadQueryPage(): IPromise<IInsightRecord[]> {

		this.state.noResults = false;

		return this.$q.when(this.insight.loadQueryPage(this.insight.query || ''))
			.then(data => {
				data = data || [];
				let items;

				if (isArray(data)) {
					items = data;
					this.totalItems = data.length;
				} else if (data) {
					items = data.items;
					this.totalItems = data.count;
				}

				this.state.noResults = (!items || !items.length) && this.insight.query;

				return items && items.map(item => {
					item._loaded = true;
					const existing = this.find(this.insight.data, item);
					return existing ? Object.assign(existing, item) : item;
				});
			});
	}

	private assignItem(item: IInsightRecord, skipModelUpdate?: boolean): void {
		this.assignItems([item], skipModelUpdate);
	}

	private assignItems(items: IInsightRecord[], skipModelUpdate?: boolean): void {
		this.assignedItems = this.assignedItems.concat(items);

		items.forEach(item => {
			item.assigned = true;
			if (this.findIndex(this.insight.data, item) === -1) {
				this.insight.data.push(item);
			}
		});

		if(!skipModelUpdate){
			this.tryUpdateModel();
		}
		else {
			this.filteredAssignedItems = this.sort(this.assignedItems.filter(i => i._loaded));
		}
	}

	private removeItem(item: IInsightRecord): void {
		item.assigned = false;

		const index = this.findIndex(this.assignedItems, item);
		if (index !== -1) {
			this.assignedItems = this.assignedItems.filter((_, i) => i !== index);
			this.tryUpdateModel();
		}
	}

	private toggleItemAssignment(item: IInsightRecord): void {
		return item.assigned ? this.removeItem(item) : this.assignItem(item);
	}

	private openOptions(): void {
		this.showOptions = true;
		this.ngModelCtrl.$setTouched();
	}

	private closeOptions(): void {
		this.showOptions = false;
		this.insight.query = '';
		this.focus = false;
	}

	private getDataType(item): string {
		const fieldDef = this.insight.fieldDefs.dataType;
		if(isFunction(fieldDef)){
			return fieldDef(item);
		}
		return item[fieldDef];
	}

	private getIconClass(item): string {
		const dataType = this.getDataType(item);

		const dataTypeClasses = this.insight.dataTypes || {};
		return dataTypeClasses[dataType] || this.insight.dataType;
	}

	private filterOptions(data: IInsightRecord[]): IInsightRecord[] {
		data = data || [];
		data = data.filter(item => item._loaded);

		if(!this.insight.loadQueryPage){
			data = this.filterFilter(data, this.insight.query);
		}
		data = this.sort(data);

		return data;
	}

	private sort(data: IInsightRecord[]): IInsightRecord[] {
		return this.orderByFilter(data, this.insight.fieldDefs.orderBy, !this.insight.fieldDefs.ascending);
	}

	private tryUpdateModel(): void {
		if (!this.ngModelCtrl) {
			return;
		}

		this.filteredAssignedItems = this.assignedItems.filter(i => i._loaded);
		this.ngModelCtrl.$setViewValue(this.assignedItems);
		this.ngModelCtrl.$setTouched();
	}

	private findIndex(array: IInsightRecord[], item: IInsightRecord): number {
		const itemId = this.getId(item);
		return array.findIndex(item => this.getId(item) === itemId);
	}

	private find(array: IInsightRecord[], item: IInsightRecord): IInsightRecord {
		const itemId = this.getId(item);
		return array.find(item => this.getId(item) === itemId);
	}

	private getId(item: IInsightRecord): string {
		return item[this.insight.fieldDefs.identifier];
	}
}
